<template>
  <div class="salon-app salon-profile-page">
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.profile.title") }}</h1>
        <SalonProfileMenu
          :initials="memberInitials"
          :name="memberName"
          @navigate="$emit('profile', $event)"
        />
      </div>
    </header>

    <main class="salon-main">
      <!-- The hero stands in for the member while the record loads, so the screen does not
           start as an empty page: the name and initials the flow already knows are enough
           to draw it, and only the tier has to wait for the call. -->
      <section class="salon-hero">
        <div class="salon-hero-avatar" aria-hidden="true">
          <span v-if="memberInitials">{{ memberInitials }}</span>
          <svg v-else viewBox="0 0 24 24" width="32" height="32">
            <path d="M12 12a4 4 0 100-8 4 4 0 000 8zm0 2c-4 0-7 2-7 4.5V20h14v-1.5c0-2.5-3-4.5-7-4.5z"
              fill="currentColor" />
          </svg>
        </div>
        <h2 class="salon-hero-name">{{ displayName || $t("salon.profile.yourProfile") }}</h2>
        <p v-if="phoneText" class="salon-hero-phone">{{ phoneText }}</p>
        <span v-if="tierName" class="salon-tier">
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.4l6.1-.8L12 3z"
              fill="currentColor" />
          </svg>
          {{ tierName }}
        </span>
      </section>

      <section class="salon-card">
        <h3 class="salon-card-title">{{ $t("salon.profile.personalInfo") }}</h3>

        <div v-if="isLoading" class="salon-skeleton" role="status" aria-live="polite">
          <span class="salon-skeleton-row"></span>
          <span class="salon-skeleton-row"></span>
          <span class="salon-skeleton-row"></span>
        </div>

        <form v-else class="salon-form" novalidate @submit.prevent="save">
          <div class="salon-field">
            <label class="salon-field-label" for="salon_profile_first_name">{{ $t("salon.profile.firstName") }}</label>
            <input
              id="salon_profile_first_name"
              ref="name"
              v-model="form.name"
              class="salon-input"
              type="text"
              name="salon_profile_first_name"
              @input="clearMessages"
              autocomplete="given-name"
              :placeholder="$t('salon.profile.firstNamePlaceholder')"
            />
          </div>

          <div class="salon-field">
            <label class="salon-field-label" for="salon_profile_last_name">{{ $t("salon.profile.lastName") }}</label>
            <input
              id="salon_profile_last_name"
              ref="last_name"
              v-model="form.last_name"
              class="salon-input"
              type="text"
              name="salon_profile_last_name"
              @input="clearMessages"
              autocomplete="family-name"
              :placeholder="$t('salon.profile.lastNamePlaceholder')"
            />
          </div>

          <div class="salon-field">
            <label class="salon-field-label" for="salon_profile_email">{{ $t("salon.profile.email") }}</label>
            <input
              id="salon_profile_email"
              ref="email"
              v-model="form.email"
              class="salon-input"
              type="email"
              name="salon_profile_email"
              @input="clearMessages"
              autocomplete="email"
              :placeholder="$t('salon.profile.emailPlaceholder')"
            />
            <p class="salon-field-hint">{{ $t("salon.profile.emailHint") }}</p>
          </div>

          <!-- MTH099 — a date of birth already on record cannot be changed from here; the
               merchant's birthday rewards are priced against it. It is rendered as text
               rather than a disabled date input because a disabled `type="date"` shows
               nothing at all for the `Y-m-d H:i:s` the API returns. -->
          <div class="salon-field">
            <label class="salon-field-label" for="salon_profile_dob">{{ $t("salon.profile.dob") }}</label>
            <div v-if="isDobLocked" class="salon-locked">
              <input
                id="salon_profile_dob"
                class="salon-input salon-input--locked"
                type="text"
                name="salon_profile_dob"
                readonly
                :value="dobDisplay"
              />
              <svg class="salon-locked-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M6 10V8a6 6 0 1112 0v2m-13 0h14v10H5V10z" fill="none" stroke="currentColor"
                  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <input
              v-else
              id="salon_profile_dob"
              ref="date_of_birth"
              v-model="form.date_of_birth"
              class="salon-input"
              type="date"
              name="salon_profile_dob"
              @change="clearMessages"
            />
            <p class="salon-field-hint">
              {{ isDobLocked ? $t("salon.profile.dobLockedHint") : $t("salon.profile.dobHint") }}
            </p>
          </div>

          <!-- Carried from the sign-in step, the same way the registration screen shows it:
               this is the number the customer proved they were using. -->
          <div class="salon-field">
            <label class="salon-field-label" for="salon_profile_mobile">{{ $t("salon.profile.mobile") }}</label>
            <div class="salon-locked">
              <input
                id="salon_profile_mobile"
                class="salon-input salon-input--locked"
                type="tel"
                name="salon_profile_mobile"
                readonly
                :value="phoneText"
              />
              <svg class="salon-locked-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M6 10V8a6 6 0 1112 0v2m-13 0h14v10H5V10z" fill="none" stroke="currentColor"
                  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
          </div>

        </form>
      </section>
    </main>

    <!-- The verdict rides with the button rather than sitting at the foot of the form: the
         bar is fixed, so a customer who saved from the top of a long card would otherwise
         never see what happened. -->
    <nav class="salon-cta">
      <p v-if="errorMessage" class="salon-error" role="alert">{{ errorMessage }}</p>
      <p v-else-if="successMessage" class="salon-success" role="status">{{ successMessage }}</p>

      <button
        class="salon-cta-btn"
        type="button"
        :disabled="isLoading || isSaving"
        @click="save"
      >
        <span>{{ isSaving ? $t("salon.profile.saving") : $t("salon.profile.save") }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import moment from "moment";
import MemberAuthService from "@/services/memberAuth.service";
import SalonProfileMenu from "@/salon/components/ProfileMenu.vue";
import "@/salon/styles/tokens.css";

/**
 * SH024 — the salon customer's own record, reached from the avatar menu.
 *
 * Like every other salon screen this is a step of BookingFlow rather than a route, so Back
 * and the menu are emitted upwards. It is a detour: nothing about the booking being made
 * is touched, and saving only writes the member.
 *
 * The record comes from the CRM api's own member endpoint — the same one MOO's Profile.vue
 * reads — because the salon summary call (member/salon/profile-summary) deliberately
 * returns neither the email nor the date of birth this screen has to edit.
 */
// Deliberately loose, matching the registration screen: the API validates properly, and a
// stricter pattern here mostly rejects legitimate addresses.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default {
  name: "SalonProfile",
  components: {
    SalonProfileMenu,
  },
  props: {
    // The avatar in the top bar, and who it stands for — same pair every salon screen takes.
    memberInitials: {
      type: String,
      default: "",
    },
    memberName: {
      type: String,
      default: "",
    },
    // Shown locked. Passed down rather than read from the record because the flow already
    // has the number the customer signed in with.
    phoneNumber: {
      type: String,
      default: "",
    },
    phoneCode: {
      type: String,
      default: "65",
    },
  },
  emits: ["back", "profile", "updated"],
  data() {
    return {
      isLoading: true,
      isSaving: false,
      errorMessage: "",
      successMessage: "",
      // As the API returned it, so isDobLocked reflects what is on record rather than what
      // is currently typed into the form.
      member: null,
      merchantMember: null,
      form: {
        name: "",
        last_name: "",
        email: "",
        date_of_birth: "",
      },
    };
  },
  computed: {
    // The form is the live answer once it has loaded; before that the name the flow
    // already knew keeps the hero from being blank.
    displayName() {
      const typed = [this.form.name, this.form.last_name]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" ");

      return typed || this.memberName;
    },

    phoneText() {
      const digits = String(this.phoneNumber || "").replace(/\D/g, "");

      if (!digits) {
        return "";
      }

      const grouped = digits.length > 4 ? digits.slice(0, 4) + " " + digits.slice(4) : digits;

      return "+" + this.phoneCode + " " + grouped;
    },

    // Only shown when the merchant actually runs tiers — otherwise every member would get
    // a badge saying nothing.
    tierName() {
      if (!this.merchantMember) {
        return "";
      }

      if (Number(this.merchantMember.allow_setup_membership_tiering) === 0) {
        return "";
      }

      return this.merchantMember.current_tier_name || "";
    },

    isDobLocked() {
      return !!(this.member && this.member.date_of_birth);
    },

    dobDisplay() {
      const dob = this.member && this.member.date_of_birth;

      return dob ? moment(dob).format("DD-MM-YYYY") : "";
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.isLoading = true;
      this.errorMessage = "";

      try {
        const response = await MemberAuthService.getMemberProfile();

        if (!response.data.result.isSuccess) {
          this.errorMessage =
            response.data.result.message || this.$t("salon.profile.loadError");
          return;
        }

        this.member = response.data.data.member || null;
        this.merchantMember = response.data.data.merchant_member || null;
        this.populate();
      } catch (error) {
        this.errorMessage = this.$t("salon.profile.networkError");
      } finally {
        this.isLoading = false;
      }
    },

    populate() {
      const member = this.member || {};

      this.form.name = member.name || "";
      this.form.last_name = member.last_name || "";
      this.form.email = member.email || "";
      // The API returns `Y-m-d H:i:s`, which a `type="date"` input rejects wholesale, so
      // only the date half is handed to it. A locked date of birth never reaches the input.
      this.form.date_of_birth = member.date_of_birth
        ? String(member.date_of_birth).slice(0, 10)
        : "";
    },

    // Typing clears the last verdict — leaving "Your profile has been updated" above a
    // field the customer is retyping reads as though the new value were already saved.
    // Bound to the inputs rather than watched on `form`, because populate() refills the
    // form right after a save and a watcher there would wipe that very confirmation.
    clearMessages() {
      this.errorMessage = "";
      this.successMessage = "";
    },

    // The message names the field so the customer is not left hunting: the salon form is
    // long enough that "Email is required" alone is not much help.
    validate() {
      if (!this.form.name.trim()) {
        return { message: this.$t("salon.profile.firstNameRequired"), field: "name" };
      }

      if (!this.form.last_name.trim()) {
        return { message: this.$t("salon.profile.lastNameRequired"), field: "last_name" };
      }

      if (!EMAIL_PATTERN.test(this.form.email.trim())) {
        return { message: this.$t("salon.profile.emailInvalid"), field: "email" };
      }

      return null;
    },

    async save() {
      if (this.isLoading || this.isSaving) {
        return;
      }

      this.successMessage = "";

      const invalid = this.validate();

      if (invalid) {
        this.errorMessage = invalid.message;
        this.$refs[invalid.field]?.focus();
        return;
      }

      this.errorMessage = "";
      this.isSaving = true;

      const payload = {
        name: this.form.name.trim(),
        last_name: this.form.last_name.trim(),
        email: this.form.email.trim(),
        date_of_birth: this.isDobLocked ? "" : this.form.date_of_birth,
      };

      try {
        const response = await MemberAuthService.updateMemberProfile(payload);

        if (!response.data.result.isSuccess) {
          this.errorMessage =
            response.data.result.message || this.$t("salon.profile.saveError");
          return;
        }

        // Re-read rather than trust the form: saving can mint a profile-completion reward
        // and move the member's tier, and the date of birth becomes locked once written.
        // It runs before the confirmation is set, because repopulating the form trips the
        // watchers that clear these messages.
        await this.load();

        this.successMessage = this.$t("salon.profile.updated");

        // The top bar's avatar and every other screen's greeting are drawn from the name
        // BookingFlow holds, so it has to hear about the change.
        this.$emit("updated", {
          name: payload.name,
          last_name: payload.last_name,
          email: payload.email,
        });
      } catch (error) {
        this.errorMessage = this.$t("salon.profile.networkError");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-profile-page {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 8px;
  background: var(--salon-surface);
  border-bottom: 2px solid var(--salon-surface-variant);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 0;
  background: transparent;
  color: var(--salon-primary);
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-lg);
  padding: var(--salon-stack-lg) var(--salon-container-padding) 132px;
}

/* ── Hero ──────────────────────────────────────────────────────── */
.salon-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--salon-stack-sm);
  text-align: center;
}

.salon-hero-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  font-family: var(--salon-font-headline);
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
  color: var(--salon-primary);
  background: var(--salon-surface-container-low);
  border: 2px solid var(--salon-secondary-container);
  border-radius: 999px;
  box-shadow: var(--salon-shadow-soft);
}

.salon-hero-name {
  margin: 0;
  /* material-dashboard forces Roboto Slab onto every h1-h3. */
  font-family: var(--salon-font-headline);
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: -0.01em;
  color: var(--salon-on-surface);
}

.salon-hero-phone {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

.salon-tier {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-secondary);
  background: var(--salon-secondary-container);
  border-radius: 999px;
}

/* ── Form card ─────────────────────────────────────────────────── */
.salon-card {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-md);
  padding: var(--salon-stack-lg) var(--salon-container-padding);
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: var(--salon-radius-card);
  box-shadow: var(--salon-shadow-soft);
}

.salon-card-title {
  margin: 0;
  /* material-dashboard forces Roboto Slab onto every h1-h3. */
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

.salon-form {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-md);
}

.salon-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.salon-field-label {
  margin-left: 4px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.06em;
  color: var(--salon-on-surface-variant);
  text-transform: uppercase;
}

.salon-field-hint {
  margin: 0 0 0 4px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-input {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  line-height: 24px;
  color: var(--salon-on-surface);
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: var(--salon-radius-input);
  box-shadow: var(--salon-shadow-soft);
  outline: none;
  appearance: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.salon-input::placeholder {
  color: var(--salon-on-surface-variant);
  opacity: 0.7;
}

.salon-input:focus {
  border-color: var(--salon-primary);
  box-shadow: 0 0 0 1px var(--salon-primary);
}

.salon-locked {
  position: relative;
  display: flex;
  align-items: center;
}

.salon-input--locked {
  padding-right: 44px;
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-container);
  cursor: not-allowed;
  box-shadow: none;
  user-select: none;
}

.salon-input--locked:focus {
  border-color: var(--salon-surface-container-highest);
  box-shadow: none;
}

.salon-locked-icon {
  position: absolute;
  right: 16px;
  color: var(--salon-on-surface-variant);
  opacity: 0.6;
  pointer-events: none;
}

.salon-error,
.salon-success {
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
}

.salon-error {
  color: var(--salon-error);
}

.salon-success {
  color: var(--salon-open-text);
}

/* ── Loading placeholder ───────────────────────────────────────── */
.salon-skeleton {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-md);
}

.salon-skeleton-row {
  height: 48px;
  background: var(--salon-surface-container-low);
  border-radius: var(--salon-radius-input);
  animation: salon-pulse 1.4s ease-in-out infinite;
}

.salon-skeleton-row:nth-child(2) {
  animation-delay: 0.15s;
}

.salon-skeleton-row:nth-child(3) {
  animation-delay: 0.3s;
}

/* ── Bottom CTA ────────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  z-index: 50;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(16px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface);
  border-top: 1px solid var(--salon-surface-variant);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-cta-btn {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-variant);
  cursor: not-allowed;
  opacity: 0.7;
}

@keyframes salon-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

@media (prefers-reduced-motion: reduce) {
  .salon-skeleton-row {
    animation: none;
  }
}
</style>
